import React, { useEffect, useRef, useState } from 'react';
import html2canvas from 'html2canvas';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Download, MessageCircle, Share2, Loader2, Sparkles } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

/**
 * ShareGardenDialog
 * - Rendert een off-screen "share card" met tuintje + branding.
 * - html2canvas → PNG blob.
 * - Opties: WhatsApp (wa.me of navigator.share), Native Share (files API), Download.
 */
export default function ShareGardenDialog({
  open,
  onClose,
  placed = [],
  catalog = [],
  stats = {}, // { stories_completed, current_streak, total_rewards }
  userId = null,
  siteUrl = 'omasverhaaltjes.nl',
}) {
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [imageUrl, setImageUrl] = useState(null); // preview
  const [blob, setBlob] = useState(null);

  useEffect(() => {
    if (!open) {
      setImageUrl(null);
      setBlob(null);
      return;
    }
    // tijd geven aan React + images om te renderen
    const t = setTimeout(async () => {
      try {
        setBusy(true);
        if (!cardRef.current) return;
        const canvas = await html2canvas(cardRef.current, {
          useCORS: true,
          allowTaint: false,
          backgroundColor: null,
          scale: 2,
          logging: false,
        });
        const url = canvas.toDataURL('image/png');
        setImageUrl(url);
        canvas.toBlob((b) => setBlob(b), 'image/png');
      } catch (err) {
        console.error('share snapshot error', err);
        toast.error('Kon geen afbeelding maken, probeer opnieuw');
      } finally {
        setBusy(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [open]);

  if (!open) return null;

  const refParam = userId ? `&ref=${encodeURIComponent(userId)}` : '';
  const shareLink = `${siteUrl}?src=garden${refParam}`;
  const textCaption = `Kijk wat ik gemaakt heb bij Oma's Verhaaltjes! 🏡✨ Lees ook jouw eigen slaapverhaaltjes op ${shareLink}`;

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const canNativeFileShare =
    canNativeShare && blob && typeof navigator.canShare === 'function' &&
    (() => {
      try {
        const f = new File([blob], 'tuintje.png', { type: 'image/png' });
        return navigator.canShare({ files: [f] });
      } catch { return false; }
    })();

  const handleDownload = () => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = 'mijn-tuintje.png';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleWhatsApp = async () => {
    // Als native file-share beschikbaar is (mobiel), gebruik dat
    if (canNativeFileShare) {
      try {
        const f = new File([blob], 'mijn-tuintje.png', { type: 'image/png' });
        await navigator.share({ files: [f], text: textCaption, title: 'Mijn Tuintje' });
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return;
      }
    }
    // Fallback: open wa.me met tekst (geen bijlage mogelijk zonder native share)
    const url = `https://wa.me/?text=${encodeURIComponent(textCaption)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleNativeShare = async () => {
    if (canNativeFileShare) {
      try {
        const f = new File([blob], 'mijn-tuintje.png', { type: 'image/png' });
        await navigator.share({ files: [f], text: textCaption, title: 'Mijn Tuintje' });
      } catch (err) {
        if (err?.name !== 'AbortError') toast.error('Delen mislukt');
      }
    } else if (canNativeShare) {
      try {
        await navigator.share({ text: textCaption, title: 'Mijn Tuintje', url: `https://${siteUrl}` });
      } catch {}
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        key="share-backdrop"
        className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="share-dialog"
      >
        <motion.div
          className="relative w-full max-w-lg bg-card rounded-3xl shadow-2xl overflow-hidden"
          initial={{ scale: 0.9, y: 40, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: 'spring', damping: 20, stiffness: 220 }}
        >
          <button
            data-testid="share-close"
            onClick={onClose}
            className="absolute top-3 right-3 z-10 text-muted-foreground hover:text-foreground rounded-full p-1.5 hover:bg-muted"
            aria-label="Sluiten"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="p-5">
            <div className="text-center mb-3">
              <h2 className="font-display text-2xl text-foreground">Deel je tuintje</h2>
              <p className="text-muted-foreground text-xs font-accent">
                Laat trots zien wat je gemaakt hebt
              </p>
            </div>

            {/* PREVIEW */}
            <div className="relative mb-4 rounded-2xl overflow-hidden bg-muted/30 min-h-[200px] flex items-center justify-center">
              {busy || !imageUrl ? (
                <div className="p-8 text-center text-muted-foreground text-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Afbeelding maken...
                </div>
              ) : (
                <img src={imageUrl} alt="Preview" className="w-full h-auto" />
              )}
            </div>

            {/* ACTIONS */}
            <div className="grid grid-cols-3 gap-2">
              <Button
                data-testid="share-whatsapp"
                onClick={handleWhatsApp}
                disabled={!blob}
                className="rounded-2xl h-auto py-3 flex flex-col gap-1 bg-[#25D366] hover:bg-[#1ebe59] text-white"
              >
                <MessageCircle className="w-5 h-5" />
                <span className="text-xs font-bold">WhatsApp</span>
              </Button>
              <Button
                data-testid="share-native"
                onClick={handleNativeShare}
                disabled={!blob || !canNativeShare}
                variant="outline"
                className="rounded-2xl h-auto py-3 flex flex-col gap-1"
              >
                <Share2 className="w-5 h-5" />
                <span className="text-xs font-bold">Delen</span>
              </Button>
              <Button
                data-testid="share-download"
                onClick={handleDownload}
                disabled={!imageUrl}
                variant="outline"
                className="rounded-2xl h-auto py-3 flex flex-col gap-1"
              >
                <Download className="w-5 h-5" />
                <span className="text-xs font-bold">Download</span>
              </Button>
            </div>

            {!canNativeShare && (
              <p className="text-[11px] text-muted-foreground text-center mt-3">
                Tip: op mobiel kun je de afbeelding direct meesturen in WhatsApp.
              </p>
            )}
          </div>
        </motion.div>

        {/* OFF-SCREEN SHARE CARD - 1080x1350 (instagram portrait) */}
        <div
          style={{
            position: 'fixed',
            left: -99999,
            top: 0,
            width: 1080,
            height: 1350,
            pointerEvents: 'none',
          }}
          aria-hidden="true"
        >
          <ShareCard innerRef={cardRef} placed={placed} stats={stats} siteUrl={siteUrl} />
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

function ShareCard({ placed, stats, siteUrl, innerRef }) {
  const s = stats || {};
  return (
    <div
      ref={innerRef}
      style={{
        width: 1080,
        height: 1350,
        background: 'linear-gradient(180deg,#fff6e9 0%,#fde5d1 100%)',
        fontFamily: 'inherit',
        padding: 48,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{
            width: 64, height: 64, borderRadius: 999,
            background: 'rgba(219,143,45,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontSize: 36 }}>📖</span>
          </div>
          <div>
            <div style={{ fontSize: 40, fontWeight: 700, color: '#1b1f30', lineHeight: 1 }}>
              Oma's Verhaaltjes
            </div>
            <div style={{ fontSize: 22, color: '#8a7769', marginTop: 6 }}>
              Mijn Tuintje
            </div>
          </div>
        </div>
      </div>

      {/* Scene */}
      <div
        style={{
          position: 'relative',
          marginTop: 32,
          flex: 1,
          borderRadius: 32,
          overflow: 'hidden',
          boxShadow: '0 16px 40px rgba(0,0,0,0.15)',
          backgroundColor: '#eaf6c5',
          backgroundImage: `url('/garden-items/scene_bg.png')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {placed.map((p) => {
          const scale = p.scale || 1;
          return (
            <div
              key={p.instance_id}
              style={{
                position: 'absolute',
                left: `${p.x}%`,
                top: `${p.y}%`,
                transform: 'translate(-50%,-80%)',
                width: `${14 * scale}%`,
                maxWidth: `${150 * scale}px`,
              }}
            >
              <img
                src={`/garden-items/${p.slug}.png`}
                alt=""
                crossOrigin="anonymous"
                style={{ width: '100%', height: 'auto', display: 'block',
                  filter: 'drop-shadow(0 6px 6px rgba(0,0,0,0.15))' }}
              />
            </div>
          );
        })}
      </div>

      {/* Footer stats + URL */}
      <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: 20 }}>
          <StatBig n={s.stories_completed || 0} label="Verhalen" color="#db8f2d" />
          <StatBig n={s.current_streak || 0} label="Streak" color="#e05c5c" />
          <StatBig n={s.total_rewards || 0} label="Kadootjes" color="#b76eff" />
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 24, color: '#8a7769' }}>Lees ook op</div>
          <div style={{ fontSize: 34, fontWeight: 700, color: '#1b1f30' }}>{siteUrl}</div>
        </div>
      </div>
    </div>
  );
}

function StatBig({ n, label, color }) {
  return (
    <div style={{
      background: 'rgba(255,255,255,0.75)',
      padding: '12px 20px',
      borderRadius: 16,
      border: `2px solid ${color}33`,
      minWidth: 100,
      textAlign: 'center',
    }}>
      <div style={{ fontSize: 42, fontWeight: 800, color, lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 20, color: '#1b1f30', marginTop: 4 }}>{label}</div>
    </div>
  );
}
